import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GRADIENTS, COLORS } from './themeConstants';

const faqs = [
	{
		question: 'What is Necttos?',
		answer: 'Necttos is an innovative education management platform that helps colleges streamline their operations, enhance student engagement, and improve administrative efficiency.',
	},
	{
		question: 'How does Necttos help educational institutions?',
		answer: 'Necttos provides comprehensive solutions including student management, attendance tracking, course management, assessment tools, and analytics to help institutions modernize their operations.',
	},
	{
		question: 'Is Necttos suitable for all types of colleges?',
		answer: 'Yes, Necttos is designed to be flexible and scalable, making it suitable for various educational institutions from small colleges to large universities.',
	},
	{
		question: 'What support does Necttos provide?',
		answer: 'We offer 24/7 technical support, regular training sessions, and dedicated account managers to ensure smooth implementation and operation.',
	},
	{
		question: 'How secure is our data with Necttos?',
		answer: 'Necttos implements industry-standard security measures including encryption, regular backups, and strict access controls to protect your institutional data.',
	},
	{
		question: 'What is the pricing model for Necttos?',
		answer: 'Our pricing model is flexible and designed to suit institutions of all sizes. Please contact our sales team for a detailed quote.',
	},
	{
		question: 'Is there a demo available for Necttos?',
		answer: "Absolutely! We offer free demos to help you understand how Necttos can revolutionize your institution's management.",
	},
];

const FAQItem = ({ question, answer, isOpen, onClick, index }) => {
	// Variants for the entire FAQ item
	const itemVariants = {
		hidden: { opacity: 0, translateY: 10 },
		visible: { opacity: 1, translateY: 0 },
	};

	// Variants for the arrow rotation
	const arrowVariants = {
		closed: { rotate: 0 },
		open: { rotate: 180 },
	};

	return (
		<motion.div className={`mb-4 ${COLORS.background.glass} rounded-lg border ${COLORS.border.glass}`} variants={itemVariants} initial='hidden' animate='visible' transition={{ delay: index * 0.1, duration: 0.5 }}>
			<button onClick={onClick} className={`w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5 transition-all duration-300 ${isOpen ? 'border-b border-gray-200/20' : ''}`}>
				<span className={`font-medium ${COLORS.text.primary}`}>{question}</span>
				<motion.span variants={arrowVariants} animate={isOpen ? 'open' : 'closed'} transition={{ duration: 0.3 }} className={`${COLORS.text.secondary}`}>
					▼
				</motion.span>
			</button>
			<AnimatePresence>
				{isOpen && (
					<motion.div key='content' initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className='overflow-hidden'>
						<div className={`px-6 py-4 ${COLORS.text.secondary}`}>{answer}</div>
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
};

const FAQ = () => {
	const [openIndex, setOpenIndex] = useState(null);
	const [isVisible, setIsVisible] = useState(false);
	const sectionRef = useRef(null);

	useEffect(() => {
		const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.1 });
		if (sectionRef.current) {
			observer.observe(sectionRef.current);
		}
		return () => {
			if (sectionRef.current) {
				observer.unobserve(sectionRef.current);
			}
		};
	}, []);

	return (
		<section ref={sectionRef} id='faq' className={`relative py-32 bg-gradient-to-br ${GRADIENTS.background.primary}`}>
			<div className='container mx-auto px-6'>
				<motion.div className='max-w-3xl mx-auto text-center mb-20' initial={{ opacity: 0, translateY: 10 }} animate={isVisible ? { opacity: 1, translateY: 0 } : {}} transition={{ duration: 0.7 }}>
					<h2 className={`text-4xl lg:text-5xl font-bold bg-gradient-to-r ${GRADIENTS.text.accent} bg-clip-text text-transparent mb-8`}>Frequently Asked Questions</h2>
					<p className={`${COLORS.text.secondary} text-lg max-w-2xl mx-auto`}>Find answers to common questions about Necttos and how we can help your institution</p>
				</motion.div>

				<div className='max-w-3xl mx-auto space-y-6'>
					{faqs.map((faq, index) => (
						<FAQItem key={index} index={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} />
					))}
				</div>
			</div>
		</section>
	);
};

export default FAQ;
